import TravelChatBot from '@mfs/client/TravelChatBot'
import { usePricePredictionEligibility } from '@mfs/client/common/hooks/usePricePredictionEligibility/usePricePredictionEligibility'
import { logOverFilter } from '@mfs/client/common/log-ga-event/logGAEvent-overfilter'
import { logEvent } from '@mfs/client/common/log-ga-event/logGaEvent'
import { logItemList } from '@mfs/client/common/log-ga-event/logGaEvent-view-item-list'
import DynamicFilters from '@mfs/client/listings-client/components/Filters/DynamicFilters/DynamicFilters'
import { usePinnedFlights } from '@mfs/client/listings-client/components/Filters/PinnedFilters/usePinnedFlights'
import { cabinClassLabels } from '@mfs/client/listings-client/constants/constants'
import useListingsFilterCallStream from '@mfs/client/listings-client/helpers/filters/useListingsFilterCallStream'
import pclnExpt from '@pcln/experimentation'
import { useFeature } from '@pcln/feature-components'
import { usePartner } from '@pcln/whitelabel-components'
import get from 'lodash/get'
import { ToastProvider } from 'pcln-design-system'
import React, { useEffect } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import ClientOnly from '../../../common-components/ClientOnly'
import { logCarouselArrow } from '../../../../client/common/log-ga-event/logGaEvent-internal-link.js'
import { logPageView } from '../../../../client/common/log-ga-event/logGaEvent-page-view.js'
import { EXPRESS_DEAL, RETAIL } from '../../../common/constants/itinerary.js'
import { AdContainer } from '../../containers/ad-container'
import { getIsShelfEligible } from '../../helpers/helpers'
import { getDeviceWidth } from '../../util/displayHelpers'
import { showPhoneSalesBanner } from '../../util/phoneSalesBanner/phoneSalesBanner'
import { InlineAd } from '../Ad/InlineAd/InlineAd'
import { BackToTop } from '../BackToTop'
import { BaggageFeeOverlay } from '../BaggageFeeOverlay'
import { ErrorOverlay } from '../ErrorOverlay'
import { FilterTextButton } from '../FilterTextButton/FilterTextButton'
import { FlexDatesWrapper } from '../FlexDates/FlexDatesWrapper'
import { getMatchingFareBrandByPriceKey, getPriceByType } from '../Listings/SBSHelpers/SBSHelpers'
import { ListingsComponentWrapper } from '../ListingsComponentWrapper/ListingsComponentWrapper'
import { LoadMoreBtn } from '../LoadMoreBtn'
import { Loader } from '../Loader'
import { LoadingText } from '../LoadingText'
import { OverfilteredBannerContainer } from '../OverfilteredBanner/OverfilteredBanner'
import { PackagePriceChangeBanner } from '../PackagePriceChangeBanner'
import { PhoneSalesBanner } from '../PhoneSalesBanner'
import { PhoneSalesFlyIn } from '../PhoneSalesFlyIn'
import { PricePredictionContainer } from '../PriceWatch/PricePredictionContainer'
import ReshopHeader from '../ReshopHeader/ReshopHeader'
import { SearchErrorScreen } from '../SearchErrorScreen'
import { ViewersFlyIn } from '../ViewersFlyIn'
import { getShouldRelocateTopStaticXsell, getShowTopStaticXsell } from './helpers/packageBannerPlacement'
import { PackageListingHeaderRow } from '../Listings/PackageListingHeaderRow'
import {
  checkIfSinglePassenger,
  getPackageListingHeaderProps,
  getPassengersCountHelper,
  isNumOfAdultPassengers,
} from './helpers.js'
import RecommendedPackageDeal from '../RecommendedPackageDeal'

const InfScrollLoadingText = styled.div`
  height: 36px;
`

/* Disable tap highlight in Chrome to prevent entire listings component from highlighting on touch
   Source: https://stackoverflow.com/a/29961714 */
const StyledListingsWrapper = styled.div`
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: transparent;
  display: ${(props) => (props.hide ? 'none' : 'block')};

  ${(props) => props.currentMobileFilter && 'visibility: hidden;'}
`

const StyledFilterTextButton = styled(FilterTextButton)`
  position: relative;
  margin-bottom: 12px;
  right: ${(props) => (props.isMobile ? '0px' : '12px')};
`

// TODO: Reason for being in global scope
const desktopWidth = 1024
const deviceWidth = getDeviceWidth()

function ContentRenderer(props) {
  const isWLPartner = Boolean(usePartner())
  const shouldForceFlyDetails = useFeature('whiteLabel.packagesForceFlyDetailsEnabled')
  const flexDatesToggle = useFeature('flights.flexDatesToggle')
  const phoneAgentFlightPlacementsEnabled = useFeature('whiteLabel.phoneAgentFlightPlacementsEnabled')
  const phoneAgentPackagePlacementsEnabled = useFeature('whiteLabel.phoneAgentPackagePlacementsEnabled')
  const overFilteredTimeStamp = useSelector((state) => state?.flags?.overFilteredTimeStamp)
  const cguid = useSelector((state) => state?.pclnBootstrapData?.requestLocals?.webstats?.clientGUID)
  const isReshopExperience = useSelector((state) => state?.pclnBootstrapData?.isReshopExperience)
  const isShelves = useSelector((state) => state?.pclnBootstrapData?.isShelves)
  const isNotReshopAndWLAndPackages = useSelector(
    (state) => state?.pclnBootstrapData?.isNotReshopAndWLAndPackages
  )
  const phoneBookInfo = useSelector((state) => state.phoneBookInfo ?? {})
  const hasUsOnlySlice = useSelector((state) => state?.pclnBootstrapData?.hasUsOnlySlice)

  const authUser = useSelector((state) => state.pclnBootstrapData?.authUser)
  const isUserLoggedIn = Boolean(authUser)
  const pclnBootstrapData = useSelector((state) => state?.pclnBootstrapData)
  const sortOptions = useSelector((state) => state?.sortOptions)

  const {
    activeTabId,
    airportToCity,
    brandReference,
    closeBaggageFeeOverlay,
    closeErrorOverlay,
    currentMobileFilter,
    deeplinkModelToken,
    departingFrom,
    displayAds,
    displayErrorPage,
    displayListings,
    displayLoadMore,
    displayNonStopItins,
    exdCarriers,
    featureConfig,
    hasUSDestinationsOnly,
    handleDepartureSliceClick,
    handleTabChange,
    isDesktop,
    isSliceSelected,
    selectedDepartureSummaries,
    listings,
    listingsPropsObject,
    listingsIsLoading,
    listingsLoadingMessage,
    listingsMetaData,
    listingsSaleStyle,
    loadMore,
    minRetailPrice,
    nonStopPrice,
    onListingItemSelect,
    onPackageListingItemSelect,
    onListingsMount,
    onSliceFilter,
    onUpsellSelect,
    isIE,
    openBaggageFeesModal,
    pclnGptDartAds,
    perPaxPricingText,
    hasEuropeFlight,
    priceGuidance,
    returnDate,
    returningFrom,
    searchKey,
    searchRefId,
    searchRequest,
    setiDetails,
    showBaggageFeeModal,
    showErrorOverlay,
    showLoadingOverlay,
    slices,
    travelDate,
    forceHeaderOpen,
    tripType,
    xSellAd,
    hasExpressDeal,
    defaultFilterAirports,
    cancelTextColor,
    cancelFooterIcon,
    vipTier,
    enableExpressDealVip,
    searchSlices,
    logExdClick,
    isPackageListings,
    packageType,
    partner,
    setPriceWatchState,
    priceWatchState,
    crossSellControlObject,
    handleHotelCrossSellClick,
    bundleType,
    purchaseWindow,
    theme,
    isHorizonListing,
    listingsHasErrored,
  } = props

  const { creditAvailable, changeFee, airlineName } = listingsMetaData?.creditAvailableSummary || {}

  const shouldRelocateTopStaticXsell = getShouldRelocateTopStaticXsell({ isDesktop, listingsPropsObject })
  const isDesktopStaticBannerPositionVariant =
    isDesktop && pclnExpt.getTestState('PKG_DESKTOP_STATIC_BANNER_POSITION') === 'VARIANT'

  const isDesktopStaticBannerPositionEligible = isDesktop && xSellAd !== undefined

  // PKG_DESKTOP_STATIC_BANNER_POSITION impression — desktop + static xsell present
  useEffect(() => {
    if (isDesktopStaticBannerPositionEligible) {
      pclnExpt.fireImpression('PKG_DESKTOP_STATIC_BANNER_POSITION')
    }
  }, [isDesktopStaticBannerPositionEligible])

  const { pinnedItins, setPinnedItins, isPinSelected, setIsPinSelected, clearAllPins } = usePinnedFlights()
  const isOnlyAdultPassengers = isNumOfAdultPassengers(searchRequest.params, isShelves)
  const isSinglePassenger = checkIfSinglePassenger(searchRequest.params)
  const passengerCount = getPassengersCountHelper(searchRequest.params)
  const modifyNow = searchRequest?.params?.userActionType === 'MODIFY'
  const isFiltersAppliedForGa = useSelector((state) => state?.flags?.isFiltersAppliedForGa)
  useListingsFilterCallStream()
  const recommendedListings = useSelector((state) => state.recommendedListings?.[state.activeTabId]) || []

  const displayDynamicFilters = !(activeTabId === 2 && tripType === 'RT' && isNotReshopAndWLAndPackages)

  // PKG_FLY_RC_LISTINGS_PRICE_PLACEMENT moves the package price above the filters,
  // so the header row renders here instead of inside Listings.
  // The default placement hides with the listings wrapper during a search, so mirror that here.
  const showHoistedPackageHeader =
    isPackageListings &&
    pclnExpt.getTestState('PKG_FLY_RC_LISTINGS_PRICE_PLACEMENT') === 'VARIANT' &&
    displayListings &&
    !showLoadingOverlay &&
    listings?.length > 0
  const { isPricePredictionEligible } = usePricePredictionEligibility()
  const isFlexDatesDisplayed =
    (tripType === 'OW' || tripType === 'RT') && activeTabId === 1 && isNotReshopAndWLAndPackages

  //Log view_item_list
  useEffect(() => {
    try {
      !listingsIsLoading &&
        tripType !== 'MD' &&
        logItemList({
          isReshopExperience,
          hasExpressDeal,
          tripType,
          departingFrom,
          returningFrom,
          listings: [...recommendedListings, ...listings],
          bundleType,
          returnDate,
          travelDate,
          activeTabId,
          inventoryAvailable: listings?.length > 0 ? 'y' : 'n',
          isSorted: sortOptions?.[1],
          searchRequestParams: searchRequest?.params,
          cabinClass: searchRequestCabinClass,
          ap: purchaseWindow,
          airportsArray: [departingFrom?.airportCode, returningFrom?.airportCode],
          isFiltersApplied: isFiltersAppliedForGa,
          searchRequest,
          hasRecommendedListings: recommendedListings.length > 0,
          isUserSignedIn: Boolean(authUser),
        })
      logPageView({
        pageType: 'listings',
        bundle: {
          flag: isReshopExperience ? 'y' : 'n',
          type: isReshopExperience ? (modifyNow ? 'exchange_now' : 'exchange_later') : null,
        },
      })
      logEvent('display', {
        type: 'search_info',
        item_name: hasExpressDeal ? (listings[0].isFused ? 'split_express' : 'express') : 'retail',
        page_name: 'listings',
        product_name: 'air',
      })
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(err)
      }
    }
  }, [listings])

  // Log overfilter
  useEffect(() => {
    if (overFilteredTimeStamp) {
      logOverFilter()
    }
  }, [overFilteredTimeStamp])

  const isFirstListingEXD = listings?.[0]?.isExpressDeal
  const listingForDealPrice = listings?.[isFirstListingEXD ? 1 : 0]
  const dealPrice = getPriceByType({
    prices: getMatchingFareBrandByPriceKey({
      fareBrands: listingForDealPrice?.fareBrands,
      priceKey: listingForDealPrice?.priceKey,
    })?.price,
    type: 'AVG_PRICE',
  })?.amount
  const allowSetPriceWatch =
    listings?.length > 0 &&
    tripType !== 'MD' &&
    !isPackageListings &&
    !priceWatchState &&
    !isReshopExperience &&
    (isUserLoggedIn || isPricePredictionEligible)
  const searchRequestCabinClass = get(searchRequest, 'params["cabin-class"]', '')

  useEffect(() => {
    if (allowSetPriceWatch) {
      setPriceWatchState({
        plfCode: partner?.plfCode || 'PCLN',
        departDate: travelDate,
        returnDate,
        tripType,
        originCityId: departingFrom?.cityId?.toString?.(),
        originCityCode: departingFrom?.cityCode,
        originCityName: departingFrom?.cityName,
        destCityId: returningFrom?.cityId?.toString?.(),
        destCityCode: returningFrom?.cityCode,
        destCityName: returningFrom?.cityName,
        destAirportCode: returningFrom?.airportCode,
        originAirportCode: departingFrom?.airportCode,
        cguid,
        dealType: isFirstListingEXD ? EXPRESS_DEAL : RETAIL,
        dealPrice,
        departure: {
          cityName: departingFrom?.cityName,
          date: travelDate,
        },
        arrival: {
          cityName: returningFrom?.cityName,
          date: tripType === 'RT' ? returnDate : null,
        },
        cabinClass: cabinClassLabels[searchRequestCabinClass],
        minRetailPrice,
      })
    }
  }, [
    allowSetPriceWatch,
    cguid,
    dealPrice,
    departingFrom?.airportCode,
    departingFrom?.cityCode,
    departingFrom?.cityId,
    departingFrom?.cityName,
    isFirstListingEXD,
    minRetailPrice,
    partner?.plfCode,
    returnDate,
    returningFrom?.airportCode,
    returningFrom?.cityCode,
    returningFrom?.cityId,
    returningFrom?.cityName,
    searchRequestCabinClass,
    setPriceWatchState,
    travelDate,
    tripType,
  ])

  const isPinVariant = tripType === 'OW'

  // Below no op callback when listing is loading will limit one API call at a time
  // when infinite scroll component call loadMoreItems multiple times
  const loadMoreItems = listingsIsLoading ? () => {} : loadMore

  const hasUpsellOptions = listingsMetaData.hasUpsellOptions

  const isShelfEligible = getIsShelfEligible(
    hasUSDestinationsOnly,
    tripType,
    hasExpressDeal,
    searchRequestCabinClass,
    deviceWidth >= desktopWidth,
    hasUpsellOptions
  )

  const isShowTotalPrice = isOnlyAdultPassengers && !isPackageListings

  const phonePlacementsEnabled =
    (isPackageListings && phoneAgentPackagePlacementsEnabled) ||
    (!isPackageListings && phoneAgentFlightPlacementsEnabled)
  const showPhoneSalesBannerContent = showPhoneSalesBanner(phoneBookInfo, phonePlacementsEnabled, {
    displayErrorPage,
  })
  // The fly-in stays available on the return slice, only the banner itself is hidden there.
  const hidePhoneSalesBanner = activeTabId === 2 && tripType === 'RT' && !hasUSDestinationsOnly
  const showPhoneSalesBannerPlacement = showPhoneSalesBannerContent && !hidePhoneSalesBanner
  const isMovePhoneBannerVariant =
    !partner && pclnExpt.getTestState('PKG_FLY_LISTINGS_MOVE_PHONE_BANNER') === 'VARIANT'
  // The listings tree is unmounted with no results and hidden while the loading overlay is up,
  // so the variant falls back to the top banner whenever the inline one cannot be seen.
  const hasVisibleListings = displayListings && listings?.length > 0 && !showLoadingOverlay
  const useInlinePhoneSalesBanner = isMovePhoneBannerVariant && hasVisibleListings
  const phoneSalesBannerProps = {
    isDesktop,
    phoneBookInfo,
    isPackageListings,
    searchRefId,
  }
  const inlinePhoneSalesBanner =
    showPhoneSalesBannerPlacement && useInlinePhoneSalesBanner ? (
      <PhoneSalesBanner {...phoneSalesBannerProps} disableSticky />
    ) : undefined

  const listingsElem =
    displayListings && listings?.length > 0 ? (
      <StyledListingsWrapper currentMobileFilter={currentMobileFilter} hide={showLoadingOverlay}>
        <ListingsComponentWrapper
          {...listingsPropsObject}
          activeTabId={activeTabId}
          anySlicesFilteredFor={isSliceSelected}
          deeplinkModelToken={deeplinkModelToken}
          CTAText={'Choose'}
          departingFrom={departingFrom}
          exdCarriers={exdCarriers}
          featureConfig={featureConfig}
          hasUSDestinationsOnly={hasUSDestinationsOnly}
          handleDepartureSliceClick={handleDepartureSliceClick}
          handleTabChange={handleTabChange}
          selectedDepartureSummaries={selectedDepartureSummaries}
          listings={listings}
          onListingItemSelect={onListingItemSelect}
          onPackageListingItemSelect={onPackageListingItemSelect}
          onListingsMount={onListingsMount}
          onSliceFilter={onSliceFilter}
          openBaggageFeesModal={openBaggageFeesModal}
          pclnGptDartAds={pclnGptDartAds}
          listingsSaleStyle={listingsSaleStyle}
          returningFrom={returningFrom}
          searchKey={searchKey}
          searchReferralId={searchRefId}
          SETI={setiDetails}
          airportToCity={airportToCity}
          minimumRetailPrice={minRetailPrice}
          nonStopPrice={nonStopPrice}
          displayNonStopItins={displayNonStopItins}
          brandReference={brandReference}
          pricingDisclosure={perPaxPricingText || ''}
          hasEuropeFlight={hasEuropeFlight}
          slices={slices}
          onUpsellSelect={onUpsellSelect}
          tripType={tripType}
          useRenderInView
          xSellAd={xSellAd}
          showStaticXsellAtBannerPosition={shouldRelocateTopStaticXsell}
          isDesktopStaticBannerPositionVariant={isDesktopStaticBannerPositionVariant}
          searchRequestCabinClass={searchRequestCabinClass}
          hasExpressDeal={hasExpressDeal}
          isShelfEligible={isShelfEligible}
          listingsMetaData={listingsMetaData}
          priceGuidanceData={priceGuidance}
          adsContainer={displayAds ? <InlineAd /> : null} // TODO: Remove after listings win
          sponsoredAdsContainer={displayAds ? <AdContainer /> : null} // TODO: Remove after listings win
          cancelTextColor={cancelTextColor}
          cancelFooterIcon={cancelFooterIcon}
          vipTier={vipTier}
          isFireFly={true}
          enableExpressDealVip={enableExpressDealVip}
          searchRequest={searchRequest}
          isMultiDestination={tripType === 'MD'}
          searchSlices={searchSlices}
          isReshopExperience={isReshopExperience}
          modifyNow={modifyNow}
          reshopPerPersonText={
            modifyNow ? 'per person including change fee' : 'per person with\n credit applied'
          }
          logCarouselArrow={logCarouselArrow}
          showLoadingOverlay={showLoadingOverlay}
          isSinglePassenger={isSinglePassenger}
          logExdClick={logExdClick}
          isShowTotalPrice={isShowTotalPrice}
          passengerCount={passengerCount}
          shouldForceFlyDetails={shouldForceFlyDetails}
          pinnedItins={pinnedItins}
          isPinSelected={isPinSelected}
          setPinnedItins={setPinnedItins}
          isPinVariant={isPinVariant}
          packageType={packageType}
          displayAds={isPackageListings}
          isDesktop={isDesktop}
          isShelves={isShelves}
          crossSellControlObject={crossSellControlObject}
          handleHotelCrossSellClick={handleHotelCrossSellClick}
          hasUsOnlySlice={hasUsOnlySlice}
          isNotReshopAndWLAndPackages={isNotReshopAndWLAndPackages}
          partner={partner}
          isHorizonListing={isHorizonListing}
          inlinePhoneSalesBanner={inlinePhoneSalesBanner}
        />
      </StyledListingsWrapper>
    ) : null

  const errorScreenPhoneNumber = isReshopExperience ? '1 (800) 774-2354' : phoneBookInfo?.phoneNumber
  const showPhoneNumber = Boolean(errorScreenPhoneNumber && !partner)
  const variantLoader =
    isNotReshopAndWLAndPackages && pclnExpt.getTestState('AIR_SEARCHES_LOAD_MODIFICATION_WEB') === 'VARIANT'

  const showTopStaticXsell = getShowTopStaticXsell({
    activeTabId,
    listingsHasErrored,
    shouldRelocateTopStaticXsell,
    isDesktopStaticBannerPositionVariant,
  })

  return (
    <div>
      {/* TODO: Do we need this */}
      {showErrorOverlay && <ErrorOverlay onClose={closeErrorOverlay} />}

      {/* Loading overlay */}
      {showLoadingOverlay && (
        <Loader
          variantLoader={variantLoader}
          searchRequest={searchRequest}
          ctaTitle={listingsLoadingMessage}
          products={isPackageListings ? pclnBootstrapData?.bundleComponents?.map((c) => c.type) : undefined}
          isShelves={isShelves}
          isNotReshopAndWLAndPackages={pclnBootstrapData?.isNotReshopAndWLAndPackages}
        />
      )}

      {/* Error screen */}
      {displayErrorPage ? (
        <SearchErrorScreen
          forceHeaderOpen={forceHeaderOpen}
          isMobile={!isDesktop}
          isPackageListings={isPackageListings}
          isReshopExperience={isReshopExperience}
          phoneBookNumber={errorScreenPhoneNumber}
          showPhoneNumber={showPhoneNumber}
          urlParams={searchRequest.params}
        />
      ) : null}

      {/* Mobile Price Watch */}
      {activeTabId !== 2 && !isDesktop && priceWatchState && (
        <ToastProvider domRootId='listing-toast-container' theme={theme} enterAnimation='slideInRight'>
          <PricePredictionContainer appCode='MOBILEWEB' {...priceWatchState} isMobile />
        </ToastProvider>
      )}

      <FlexDatesWrapper />

      {showTopStaticXsell && <RecommendedPackageDeal isFlexDatesDisplayed={isFlexDatesDisplayed} />}

      {isReshopExperience && (
        <ReshopHeader
          airlineName={airlineName}
          modifyNow={modifyNow}
          creditAvailable={creditAvailable}
          changeFee={changeFee}
        />
      )}

      {/* Baggage fee overlay */}
      <BaggageFeeOverlay
        isOpen={showBaggageFeeModal}
        onClose={closeBaggageFeeOverlay}
        forceiOSOverflowVisible={true}
      />

      {!isReshopExperience &&
      !isWLPartner &&
      !showPhoneSalesBanner(phoneBookInfo, phonePlacementsEnabled) &&
      tripType !== 'MD' &&
      defaultFilterAirports &&
      flexDatesToggle &&
      !isPackageListings ? (
        <ClientOnly>
          <ViewersFlyIn
            defaultFilterAirports={defaultFilterAirports}
            returningFrom={returningFrom}
            travelDate={travelDate}
            isIE={isIE}
          />
        </ClientOnly>
      ) : null}

      {showPhoneSalesBannerContent && !isPackageListings ? (
        <ClientOnly>
          <PhoneSalesFlyIn isDesktop={isDesktop} phoneBookInfo={phoneBookInfo} searchRefId={searchRefId} />
        </ClientOnly>
      ) : null}

      <PackagePriceChangeBanner
        isPackageListings={isPackageListings}
        bundleComponents={pclnBootstrapData?.bundleComponents}
        displayListings={displayListings}
        showLoadingOverlay={showLoadingOverlay}
        listings={listings}
        recommendedListings={recommendedListings}
        activeTabId={activeTabId}
        hasPriorPackageSelection={pclnBootstrapData?.hasPriorPackageSelection === true}
      />

      {showHoistedPackageHeader ? (
        <PackageListingHeaderRow
          {...getPackageListingHeaderProps({
            listings,
            recommendedListings,
            activeTabId,
            packageType,
            searchRequest,
            searchSlices,
            hasPriorPackageSelection: pclnBootstrapData?.hasPriorPackageSelection === true,
            isProposalPricingMissing: listingsMetaData?.isProposalPricingMissing === true,
          })}
        />
      ) : null}

      {showPhoneSalesBannerPlacement && !useInlinePhoneSalesBanner ? (
        <PhoneSalesBanner {...phoneSalesBannerProps} />
      ) : null}

      {displayDynamicFilters ? (
        <DynamicFilters
          pinCount={pinnedItins.size}
          handlePinSelect={() => {
            if (pinnedItins.size > 0) setIsPinSelected(!isPinSelected)
          }}
          isPinSelected={isPinSelected}
          isPinVariant={isPinVariant}
        />
      ) : null}

      {pinnedItins.size > 1 && isPinSelected && (
        <StyledFilterTextButton
          isMobile={!isDesktop}
          onClick={() => {
            clearAllPins()
          }}
        >
          Clear all Pins
        </StyledFilterTextButton>
      )}

      <OverfilteredBannerContainer />

      {/* Listings */}
      <InfiniteScroll
        loader={
          displayListings &&
          !showLoadingOverlay && (
            <InfScrollLoadingText>
              <LoadingText />
            </InfScrollLoadingText>
          )
        }
        /* Temporarily disabling the infinite scroll until an alternate library is found */
        hasMore={false}
        next={loadMoreItems}
        style={{ overflow: 'unset !important' }}
      >
        {listingsElem}
      </InfiniteScroll>

      <TravelChatBot
        details={{
          loggingInfo: {
            travelDate,
            tripType,
            returnDate,
            numOfAdults: Number(searchRequest?.params?.['num-adults']) || 0,
            numOfChildren: Number(searchRequest?.params?.['num-children']) || 0,
            numOfLapInfants: Number(searchRequest?.params?.['num-infants']) || 0,
            cabinClass: searchRequest?.params?.['cabin-class'] || '',
            slicesUrl: (searchRequest?.slicesURL || '').replace(/^\/|\/$/g, ''),
            searchType: searchRequest?.params?.['search-type'] || '',
            searchKey,
          },
          listings,
        }}
        pclnBootstrapData={pclnBootstrapData}
        isProgress={listingsIsLoading}
        pageSource='listings'
      />
      {/* Back to Top */}
      {displayListings && !showLoadingOverlay && <BackToTop />}

      {/* Load More */}
      {!showLoadingOverlay &&
      displayLoadMore &&
      !overFilteredTimeStamp &&
      !isPinSelected &&
      listings?.length > 0 ? (
        <ClientOnly>
          <LoadMoreBtn requestPending={listingsIsLoading} onClick={loadMoreItems} />
        </ClientOnly>
      ) : null}
    </div>
  )
}

export { ContentRenderer as default } // lazy loaded, keeping default
